/**
 * Data access for the whole dashboard.
 *
 * The snapshot is polled at the backend's own interval, because polling faster than
 * the poller only re-reads the same cache. `keepPreviousData` is what lets the UI
 * hold its last render at reduced opacity while a refetch is in flight - no skeleton,
 * no layout jump, no flash on a dashboard someone is watching.
 *
 * Ad-hoc probes (the refresh button, the manual host form) write their results
 * straight into the snapshot cache, so a probed host appears immediately instead of
 * waiting out the next poll.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api/client'
import type {
  LatencyThresholds,
  ManualTargetUpdate,
  NewCategoryRequest,
  ProbeRequest,
  Snapshot,
  TargetMode,
  TargetResult,
} from '../api/types'

const SNAPSHOT_KEY = ['snapshot'] as const
const CONFIG_KEY = ['config'] as const

/**
 * The bands to colour by until /config answers, and if a site names none.
 *
 * Mirrors DEFAULT_LATENCY_THRESHOLDS in backend/app/config.py - the backend is the one
 * that decides, so this only covers the first render before its answer arrives.
 */
const FALLBACK_THRESHOLDS: LatencyThresholds = {
  warning: 100,
  serious: 500,
  critical: 2000,
}

/** Static-ish: only changes when sites.json or the environment does. */
export function useConfig() {
  return useQuery({
    queryKey: CONFIG_KEY,
    queryFn: api.config,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSnapshot(autoRefresh: boolean, intervalSeconds: number) {
  return useQuery({
    queryKey: SNAPSHOT_KEY,
    queryFn: () => api.snapshot(false),
    // Matched to the backend's poll interval; a shorter interval would only re-read
    // the same cached results.
    refetchInterval: autoRefresh ? Math.max(5, intervalSeconds) * 1000 : false,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  })
}

/**
 * Merge probe results into the cached snapshot.
 *
 * Results replace by `target_id` and unseen targets are appended, so a manual host
 * joins the grid without a refetch. The roll-up is deliberately *not* patched here -
 * it is recomputed by the next snapshot from the full set, and half-updating it would
 * put a headline on screen that disagrees with the cards below it.
 */
function mergeResults(previous: Snapshot | undefined, results: TargetResult[],
                      history: Snapshot['history']): Snapshot | undefined {
  if (!previous) return previous

  const byId = new Map(previous.results.map((result) => [result.target_id, result]))
  for (const result of results) byId.set(result.target_id, result)

  const merged = [...byId.values()].sort(
    (a, b) =>
      a.site_name.localeCompare(b.site_name) || a.env_label.localeCompare(b.env_label),
  )

  return {
    ...previous,
    results: merged,
    history: { ...previous.history, ...history },
  }
}

export function useProbe() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: ProbeRequest) => api.probe(body),
    onSuccess: (response) => {
      queryClient.setQueryData<Snapshot>(SNAPSHOT_KEY, (previous) =>
        mergeResults(previous, response.results, response.history),
      )
      // Then let the authoritative snapshot land, so the roll-up and the cards agree.
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY })
    },
  })
}

/** The refresh button: re-probe every configured target now. */
export function useRefreshAll() {
  const probe = useProbe()
  const refresh = useCallback(
    () => probe.mutateAsync({ all_configured: true }),
    [probe],
  )
  return { refresh, isPending: probe.isPending, error: probe.error }
}

/**
 * Delete a manual monitor.
 *
 * The card is removed from the cached snapshot immediately rather than after a refetch,
 * because a delete that leaves its card on screen for a second reads as having failed.
 * The invalidation that follows lets the authoritative snapshot correct the roll-up.
 */
export function useDeleteTarget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (targetId: string) => api.deleteTarget(targetId),
    onSuccess: (_data, targetId) => {
      queryClient.setQueryData<Snapshot>(SNAPSHOT_KEY, (previous) => {
        if (!previous) return previous
        const { [targetId]: _removed, ...history } = previous.history
        return {
          ...previous,
          results: previous.results.filter((result) => result.target_id !== targetId),
          history,
        }
      })
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY })
    },
  })
}

export function useClearHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targetId?: string) => api.clearHistory(targetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY }),
  })
}

/**
 * Edit a manual monitor.
 *
 * The snapshot is invalidated on success so the (possibly renamed) card reflects the new
 * config. When the edit changes host/path/scheme the target id changes, so the old id's
 * cached result is dropped to avoid a lingering stale card until the next poll.
 */
export function useUpdateManualTarget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ targetId, body }: { targetId: string; body: ManualTargetUpdate }) =>
      api.updateManualTarget(targetId, body),
    onSuccess: (updated, { targetId }) => {
      if (updated.id !== targetId) {
        queryClient.setQueryData<Snapshot>(SNAPSHOT_KEY, (previous) => {
          if (!previous) return previous
          const { [targetId]: _removed, ...history } = previous.history
          return {
            ...previous,
            results: previous.results.filter((r) => r.target_id !== targetId),
            history,
          }
        })
      }
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY })
    },
  })
}

/**
 * Switch a target's mode. The backend re-probes on the change, so invalidating the
 * snapshot picks up the new view (public vs detailed) without a second round trip here.
 */
export function useSetMode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ targetId, mode }: { targetId: string; mode: TargetMode }) =>
      api.setMode(targetId, mode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY }),
  })
}

/**
 * Check the key a target already holds.
 *
 * No cache invalidation on success, deliberately: the check issues nothing and discards
 * nothing, so no card's state has changed and refetching the snapshot would only make the
 * grid flicker for an answer that lives entirely in the card that asked for it.
 */
export function useCheckApiKey() {
  return useMutation({
    mutationFn: (targetId: string) => api.checkApiKey(targetId),
  })
}

/** Set or clear a target's own poll interval. */
export function useSetInterval() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ targetId, seconds }: { targetId: string; seconds: number | null }) =>
      api.setInterval(targetId, seconds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['target-settings'] }),
  })
}

/**
 * Change the global default poll interval.
 *
 * Both caches are written, not just the config's, and that is the whole point.
 * `useDashboard` resolves the interval from the *snapshot* first - it is the one the
 * running monitor is actually using - so updating only the config leaves the stale
 * snapshot value winning, and the picker springs back to the old number. It would
 * correct itself when the next snapshot landed, except that lands one *old* interval
 * away: change 10m to 10s and the dashboard keeps saying 10m, and keeps polling at it,
 * for ten more minutes. Only a page reload looked like it worked, because that refetches
 * both. Patching the snapshot too makes the new cadence take effect on the same tick.
 *
 * The value written is the one the server returned, never the one requested - the server
 * is what decides (it rejects anything under 5s outright), so echoing the request would
 * risk putting a number on screen that the monitor is not using. The invalidation that
 * follows lets the real snapshot confirm it.
 */
export function useSetGlobalInterval() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (seconds: number) => api.setGlobalInterval(seconds),
    onSuccess: (config) => {
      queryClient.setQueryData(CONFIG_KEY, config)
      queryClient.setQueryData<Snapshot>(SNAPSHOT_KEY, (previous) =>
        previous
          ? { ...previous, poll_interval_seconds: config.poll_interval_seconds }
          : previous,
      )
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY })
    },
  })
}

/** Add a category. The config query is replaced with the returned one, and the next
 *  snapshot poll shows the new cards. */
export function useAddCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: NewCategoryRequest) => api.addCategory(body),
    onSuccess: (config) => {
      queryClient.setQueryData(CONFIG_KEY, config)
      void queryClient.invalidateQueries({ queryKey: SNAPSHOT_KEY })
    },
  })
}

/** Until the config lands, poll at the same default the backend ships with. */
const FALLBACK_INTERVAL_SECONDS = 30

/**
 * Everything the page needs, in one call.
 *
 * The poll interval is resolved here rather than passed in, so the UI cannot end up
 * polling faster than the backend's own loop - refetching more often than the poller
 * runs would only re-read the same cached snapshot. The snapshot's value wins over the
 * config's, since it is the interval the running monitor is actually using - which is
 * why anything that changes that interval has to write the snapshot cache as well as
 * the config's, or the stale value here keeps winning. See `useSetGlobalInterval`.
 *
 * `isRefetching` is surfaced separately from `isLoading` because the two call for
 * opposite treatments: the first load has nothing to show and gets a message, while a
 * refetch already has a render worth keeping and gets held at reduced opacity.
 */
export function useDashboard(autoRefresh: boolean) {
  const config = useConfig()

  const [intervalSeconds, setIntervalSeconds] = useState(FALLBACK_INTERVAL_SECONDS)
  const snapshot = useSnapshot(autoRefresh, intervalSeconds)

  const resolvedInterval =
    snapshot.data?.poll_interval_seconds ??
    config.data?.poll_interval_seconds ??
    FALLBACK_INTERVAL_SECONDS

  useEffect(() => {
    setIntervalSeconds(resolvedInterval)
  }, [resolvedInterval])

  const sitesById = useMemo(
    () => new Map((config.data?.sites ?? []).map((site) => [site.id, site])),
    [config.data],
  )

  return {
    config: config.data,
    configError: config.error,
    snapshot: snapshot.data,
    snapshotError: snapshot.error,
    sitesById,
    pollIntervalSeconds: resolvedInterval,
    thresholds: config.data?.latency_thresholds_ms ?? FALLBACK_THRESHOLDS,
    isLoading: config.isLoading || (snapshot.isLoading && !snapshot.data),
    isRefetching: snapshot.isFetching && Boolean(snapshot.data),
    dataUpdatedAt: snapshot.dataUpdatedAt,
  }
}
